/**
 * @file 阶段卷打印模板
 * @description 对应客户端 PrintTemplate：题目列表、答案与二维码。
 *
 * @whenToUse 试卷 2（paper2）的打印预览。
 * @whenNotToUse 教案卷与精简教案卷。
 */

import type { CSSProperties } from 'react'
import { QuestionType } from '@/enum/common.ts'
import {
  buildPaperAnswerQrCodeDataUrl,
  type HandoutPaperModel,
} from '@/components/pages/handout-page/print/paper-data.ts'
import { PaperPages } from '@/components/pages/handout-page/print/paper-pages.tsx'
import type { PaperPaginator } from '@/components/pages/handout-page/print/paginate.ts'
import { usePaperLayout } from '@/components/pages/handout-page/print/use-paper-layout.ts'

const STAGE_HEADER_STYLE = { '--mb': '3mm' } as CSSProperties

/**
 * 按阶段卷顺序分页。
 * @param pager 分页器
 * @param model 试卷
 */
async function fillStage(pager: PaperPaginator, model: HandoutPaperModel) {
  const questions = model.stageQuestions
  for (let index = 0; index < questions.length; index += 1) {
    const question = questions[index]
    await pager.add({
      type: 'question',
      isLast: index === questions.length - 1,
      data: {
        index: index + 1,
        question: question.questionHtml,
        needPlaceholder:
          question.type === QuestionType.APPLICATION || question.type === QuestionType.SHORT_ANSWER,
        options: question.type === QuestionType.SELECT ? question.options ?? null : null,
      },
    })
  }

  if (!model.printAnswers) return

  if (questions.length > 0) {
    await pager.breakPage()
    await pager.add({ type: 'answer-prefix' })
    await pager.add({
      type: 'block_title',
      data: { title: '答案', description: '' },
    })
    for (let index = 0; index < questions.length; index += 1) {
      const question = questions[index]
      await pager.add({
        type: 'answer-data',
        isLast: index === questions.length - 1,
        data: {
          index: index + 1,
          answerHtml: question.answerHtml,
          analysisHtml: question.analysisHtml,
        },
      })
    }
  }

  if (model.header.paperNo) {
    const qrCodeDataUrl = await buildPaperAnswerQrCodeDataUrl(model.header.paperNo)
    await pager.add({
      type: 'paper-qr-code',
      data: { paperNo: model.header.paperNo, qrCodeDataUrl },
    })
  }
}

/**
 * 阶段卷（试卷 2）。
 */
export function PrintTemplate({
  model,
  onReady,
}: {
  /** 试卷数据 */
  model: HandoutPaperModel
  /** 分页结束 */
  onReady?: (ready: boolean) => void
}) {
  const layout = usePaperLayout('stage', model, fillStage, onReady)
  return (
    <PaperPages
      pages={layout.pages}
      ready={layout.ready}
      rootRef={layout.rootRef}
      containers={layout.containers}
      header={model.header}
      headerStyle={STAGE_HEADER_STYLE}
    />
  )
}
